import isNull from 'lodash/isNull';
import { useContext, useEffect, useState } from 'react';

import { BrandContext } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import useQuery from 'hooks/useQuery';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const controlGroup = { isVariant: false };

/**
 * A hook for assigning a user into an experiment and retrieving the active
 * experiment group for a given experiment.
 *
 * See doc/experiments/README.md for more details.
 *
 * @typedef {Object} ExperimentData
 * @property {boolean} isVariant Whether the experiment group is a variant group
 * @property {string} experimentName The name of the experiment
 * @property {string} [experimentGroupId] (optional) the hash ID of the experiment group
 *
 * @param {Object} args
 * @param {string} args.name The name of the experiment (see: lib/constants/experimentConstants.js)
 * @param {string} [args.excludedBrand=null] (optional) Brand to exclude from experiment (see: app/javascript/context/BrandContext.js)
 * @param {Array} [args.additionalConditions=[]] (optional) Array of booleans; if any are false the user will not be assigned to the experiment
 * @param {Array} [dependencies=[]] An array of dependencies on which to re-run assignment
 * @returns {ExperimentData}
 *
 */
const useAssignExperiment = (
  { name, excludedBrand = null, additionalConditions = [] },
  dependencies = []
) => {
  localStorage.delete('experiments'); // Delete outdated experiments object
  localStorage.delete('experiments_v2'); // Delete outdated experiments object

  const [experimentAssignment, setExperimentAssignment] = useState();
  const { brand } = useContext(BrandContext);
  const { queryParams } = useQuery();

  const experimentParam = queryParams.get(name);
  const brandIsExcluded = isNull(brand) ? true : brand === excludedBrand;
  const allAdditionalConditionsMet = additionalConditions.every(condition => !!condition);

  useEffect(() => {
    if (experimentParam || brandIsExcluded || !allAdditionalConditionsMet) return;

    const createExperimentAssignment = async () => {
      try {
        const response = await RequestService.createExperimentAssignment({ params: { name } });
        if (response.ok) {
          setExperimentAssignment(response.experimentAssignment);
        } else {
          ErrorLogging.captureException(response.error);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    createExperimentAssignment();
  }, [allAdditionalConditionsMet, brandIsExcluded, experimentParam, name, ...dependencies]);

  useEffect(() => {
    const getExperiment = async () => {
      const response = await RequestService.getExperiment({ params: { name } });
      if (response.ok && response.experiment) {
        const group = response.experiment.groups.find(group => group.id === experimentParam);
        if (group) {
          setExperimentAssignment({ isVariant: group.variant, experimentGroupId: group.id });
        }
      }
    };

    if (experimentParam) getExperiment();
  }, [experimentParam, setExperimentAssignment]);

  if (brandIsExcluded || !allAdditionalConditionsMet) {
    return controlGroup;
  } else {
    return experimentAssignment ? experimentAssignment : controlGroup;
  }
};

export default useAssignExperiment;
